import { useState, type FC } from 'react'; 
import './PaymentModal.scss';
import { HiX, HiOutlineExclamationCircle, HiOutlineCheckCircle } from 'react-icons/hi';
import logoImage_1 from '../../assets/White PNG 1.png'; 
import logoImage_2 from '../../assets/Black PNG 1.png'; 

const API_BASE_URL = 'https://api.neontradepro.com';

interface Plan {
  id: number;
  name: string;
  price: number;
  period: string;
}

interface PaymentModalProps {
  plan: Plan | null;
  onClose: (status?: 'success' | 'cancel') => void;
  // invoiceId may be provided (paying an existing invoice) or omitted
  invoiceId?: string | number | null;
  // when creating invoice inside modal these values are required
  planDuration?: number;
  tradeAccountId?: number;
  discountCode?: string | null;
  theme: string;
}

type PaymentStep = 'details' | 'connecting' | 'confirming' | 'success' | 'error';

const PaymentModal: FC<PaymentModalProps> = ({ plan, onClose, invoiceId, planDuration, tradeAccountId, discountCode, theme }) => {
  const [step, setStep] = useState<PaymentStep>('details');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [internalInvoiceId, setInternalInvoiceId] = useState<string | null>(invoiceId ? String(invoiceId) : null);

  if (!plan) {
    console.error("PaymentModal: Missing plan. This modal should not be rendered without it.");
    return null;
  }

  const handlePay = async () => {
    setLoading(true);
    setErrorMessage('');

    const token = localStorage.getItem('token');
    if (!token) {
      setErrorMessage('توکن احراز هویت یافت نشد. لطفاً وارد شوید.');
      setStep('error');
      setLoading(false);
      return;
    }

    try {
      // If we don't have an invoice id yet, create one now (user pressed پرداخت in summary)
      let invId = internalInvoiceId;
      if (!invId) {
        // validate required params
        if (!planDuration || !tradeAccountId) {
          throw new Error('اطلاعات فاکتور ناقص است. لطفاً از صفحه خرید اقدام کنید.');
        }
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('توکن احراز هویت یافت نشد. لطفاً وارد شوید.');
        }
        const body: any = {
          plan_id: plan.id,
          plan_duration: planDuration,
          trade_account_id: tradeAccountId
        }
        if (discountCode) body.discount_code = discountCode

        const createResp = await fetch(`${API_BASE_URL}/plan/buy`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
        if (!createResp.ok) throw new Error((await createResp.json()).detail || 'خطا در صدور فاکتور.')
        const createData = await createResp.json()
        if (!createData.invoice_id) throw new Error('شناسه فاکتور دریافت نشد.');
        invId = String(createData.invoice_id);
        setInternalInvoiceId(invId);
      }

      const response = await fetch(`${API_BASE_URL}/invoice/plan/${invId}/pay`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === "plan_invoice_mismatch"){
          throw new Error('مبلغ پرداختی شما با مبلغ صورتحساب همخوانی ندارد. لطفا به پشتیبانی تیکت بزنید.');
        }
        throw new Error(errorData.detail || 'خطا در شروع فرآیند پرداخت.');
      }

      const paymentData = await response.json();

      if (paymentData.ok === true) {
        setStep('success');
        setLoading(false);
        return;
      }

      const paymentUrl = paymentData.payment_url; 

      if (paymentUrl) {
        window.location.href = paymentUrl; 
      } else {
        setErrorMessage('آدرس درگاه پرداخت از سرور دریافت نشد.');
        setStep('error');
      }

    } catch (err: any) {
      console.error('Failed to initiate payment:', err);
      setErrorMessage(err.message || 'خطا در برقراری ارتباط با سرور پرداخت.');
      setStep('error');
    } finally {
      if (step !== 'success') {
         setLoading(false);
      }
    }
  };

  const renderContent = () => {
    switch (step) {
      case 'error':
        return (
          <div className="step-content">
            <HiOutlineExclamationCircle className="icon-large error" />
            <h3>خطا!</h3>
            <p>{errorMessage}</p>
            <button className="modal-btn" onClick={() => onClose('cancel')}>بستن</button>
          </div>
        );

      case 'success':
        return (
          <div className="step-content">
            <HiOutlineCheckCircle className="icon-large success" />
            <h3>عملیات موفق</h3>
            <p>سفارش شما با موفقیت ثبت شد.</p>
            <p style={{fontSize: '0.9rem', color: 'var(--modal-text-muted)'}}>
              جهت فعال‌سازی نهایی و تایید ادمین، روی دکمه زیر کلیک کنید تا به تنظیمات پلن هدایت شوید.
            </p>
            <button className="modal-btn primary" onClick={() => {
              onClose('success')
              window.location.href = `https://neontradepro.com/invoice/plan/callback?id=${internalInvoiceId}`;
            }}>
              ادامه و تنظیمات پلن
            </button>
          </div>
        );

      case 'details':
      default:
        return (
          <div className="step-content">
            <div className="plan-summary">
              <h4>خلاصه سفارش</h4>
              <p><span>پلن:</span> <strong>{plan.name}</strong></p>
              <p><span>مبلغ:</span> <strong className="price">${plan.price} USDT</strong></p>
            </div>
            
            <button className="modal-btn primary" onClick={handlePay} disabled={loading}>
              {loading ? 'در حال پردازش...' : 'پرداخت'}
            </button>
          </div>
        );
    }
  };

  return (
    <div className={`modal-backdrop ${theme}`} onClick={() => onClose('cancel')}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <img src={theme === 'dark' ? logoImage_1 : logoImage_2} alt="Logo" className="modal-logo" />
          <button className="close-btn" onClick={() => onClose('cancel')}><HiX /></button>
        </div>
        <div className="modal-body">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;